import * as React from "react"

import { Check } from "lucide-react"

import { cn } from "../../cn"

export interface ChoiceCardProps extends Omit<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  "title" | "onSelect"
> {
  /** Primary text. */
  title: React.ReactNode
  /** Secondary line under the title. */
  description?: React.ReactNode
  /** Rendered inline after the title, e.g. a Badge. */
  badge?: React.ReactNode
  /** Extra content under the description, e.g. more badges or a summary line. */
  children?: React.ReactNode
  selected: boolean
  onSelect: () => void
  /**
   * A control rendered at the card's right edge, outside the select button so
   * the two never nest. Typically a preview or info button.
   */
  trailing?: React.ReactNode
  /** Class applied to the outer wrapper rather than the button. */
  wrapperClassName?: string
  /**
   * "card" carries the selected and hover states a list of candidates needs.
   * "plain" is the single already-made choice shown inside a section that is
   * itself a card: the divider-coloured outline stays, the fill does not.
   */
  variant?: "card" | "plain"
}

interface ChoiceCardGroupContextValue {
  /** The card holding the group's single tab stop, by its `data-choice-card` id. */
  tabStopId: string | null
  /** Re-picks the tab stop after a card's selection or disabled state changes. */
  recompute: () => void
}

const ChoiceCardGroupContext =
  React.createContext<ChoiceCardGroupContextValue | null>(null)

type ChoiceCardGroupProps = Omit<React.HTMLAttributes<HTMLDivElement>, "role">

/**
 * A single-choice set of ChoiceCards, exposed as a radio group. Cards inside it
 * are radios with one tab stop: the selected card, or the first enabled card
 * while none is selected or the selected one is disabled. The arrow keys, Home
 * and End move to and select a sibling. Name the group with `aria-label` or
 * `aria-labelledby`.
 */
const ChoiceCardGroup = React.forwardRef<HTMLDivElement, ChoiceCardGroupProps>(
  (props, ref) => {
    const containerRef = React.useRef<HTMLDivElement | null>(null)
    const [tabStopId, setTabStopId] = React.useState<string | null>(null)

    // Read from the DOM so the pick follows document order and each card's
    // rendered state, whatever wraps the cards.
    const recompute = React.useCallback(() => {
      const container = containerRef.current
      if (!container) {
        return
      }
      const radios = Array.from(
        container.querySelectorAll<HTMLButtonElement>(
          "[data-choice-card]:not(:disabled)",
        ),
      )
      const stop =
        radios.find((radio) => radio.getAttribute("aria-checked") === "true") ??
        radios.at(0)
      setTabStopId(stop?.dataset.choiceCard ?? null)
    }, [])

    // Cards' layout effects run before this container's ref is attached, so
    // the first pick happens here once the whole group is mounted.
    React.useLayoutEffect(() => {
      recompute()
    }, [recompute])

    const setRef = React.useCallback(
      (node: HTMLDivElement | null) => {
        containerRef.current = node
        if (typeof ref === "function") {
          ref(node)
        } else if (ref) {
          ref.current = node
        }
      },
      [ref],
    )

    const context = React.useMemo<ChoiceCardGroupContextValue>(
      () => ({ tabStopId, recompute }),
      [tabStopId, recompute],
    )

    return (
      <ChoiceCardGroupContext.Provider value={context}>
        <div ref={setRef} {...props} role="radiogroup" />
      </ChoiceCardGroupContext.Provider>
    )
  },
)

/**
 * Arrow keys, Home and End move focus to a sibling radio in the same group and
 * select it, as a native radio group does.
 */
function moveWithinGroup(event: React.KeyboardEvent<HTMLButtonElement>) {
  const group = event.currentTarget.closest('[role="radiogroup"]')
  if (!group) {
    return
  }
  const radios = Array.from(
    group.querySelectorAll<HTMLButtonElement>(
      "[data-choice-card]:not(:disabled)",
    ),
  )
  const current = radios.indexOf(event.currentTarget)
  const count = radios.length
  let next: number
  switch (event.key) {
    case "ArrowDown":
    case "ArrowRight":
      next = (current + 1) % count
      break
    case "ArrowUp":
    case "ArrowLeft":
      next = (current - 1 + count) % count
      break
    case "Home":
      next = 0
      break
    case "End":
      next = count - 1
      break
    default:
      return
  }
  event.preventDefault()
  radios[next].focus()
  radios[next].click()
}

ChoiceCardGroup.displayName = "ChoiceCardGroup"

/**
 * One option in a picker: the whole card is the select control, with a check
 * on the selected one. Used wherever a choice has enough to say that a radio
 * label would not fit, such as voices and audio presets. Inside a
 * ChoiceCardGroup it is a radio; on its own it is a toggle button.
 */
const ChoiceCard = React.forwardRef<HTMLButtonElement, ChoiceCardProps>(
  (
    {
      title,
      description,
      badge,
      children,
      selected,
      onSelect,
      trailing,
      disabled,
      className,
      wrapperClassName,
      variant = "card",
      onKeyDown,
      ...props
    },
    ref,
  ) => {
    const group = React.useContext(ChoiceCardGroupContext)
    const cardId = React.useId()
    const recompute = group?.recompute

    React.useLayoutEffect(() => {
      if (!recompute) {
        return
      }
      recompute()
      // On unmount the card may still be in the DOM, so the pick waits for
      // the commit to finish.
      return () => queueMicrotask(recompute)
    }, [recompute, selected, disabled])

    const holdsTabStop =
      group?.tabStopId == null
        ? selected && !disabled
        : group.tabStopId === cardId

    const semantics = group
      ? {
          role: "radio",
          "aria-checked": selected,
          tabIndex: holdsTabStop ? 0 : -1,
          onKeyDown: (event: React.KeyboardEvent<HTMLButtonElement>) => {
            onKeyDown?.(event)
            if (!event.defaultPrevented) {
              moveWithinGroup(event)
            }
          },
        }
      : { "aria-pressed": selected, onKeyDown }

    return (
      <div className={cn("relative", wrapperClassName)}>
        <button
          ref={ref}
          type="button"
          data-choice-card={cardId}
          disabled={disabled}
          onClick={onSelect}
          className={cn(
            "flex min-h-14 w-full flex-col justify-center gap-1 rounded-md p-3 text-left transition-colors",
            trailing && "pr-14",
            // Only the chosen card is filled, so the selection is what the eye
            // lands on rather than every candidate reading as a raised box.
            variant === "card" &&
              (selected
                ? "border border-coval-border-active bg-surface-0"
                : "border border-coval-border hover:border-coval-border-active hover:bg-surface-default-hover"),
            variant === "plain" &&
              "border border-coval-border hover:bg-surface-default-hover",
            disabled && "opacity-60",
            className,
          )}
          {...props}
          // The selection semantics and roving tab stop belong to the card and
          // its group, so a caller's role, tabIndex or aria state cannot
          // contradict what the arrow keys do.
          {...semantics}
        >
          <div className="flex min-w-0 items-center gap-1.5">
            <span className="truncate text-sm font-semibold text-coval-text">
              {title}
            </span>
            {badge}
            {/* The check carries the selected state: the card's edge and
                fill are too close to an unselected card's to identify it. */}
            {selected && (
              <Check
                aria-hidden="true"
                className="size-4 shrink-0 text-coval-contrast"
              />
            )}
          </div>
          {description && (
            <p className="text-xs text-coval-text-muted">{description}</p>
          )}
          {children}
        </button>
        {trailing && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            {trailing}
          </div>
        )}
      </div>
    )
  },
)

ChoiceCard.displayName = "ChoiceCard"

export { ChoiceCard, ChoiceCardGroup }
